import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getForms, deleteForm } from "@/lib/api";
import { saveAs } from 'file-saver';
import { useAuth } from "@/contexts/AuthContext";
import {
  Search,
  FileText,
  Clock,
  CheckCircle2,
  Send,
  Trash2,
  Edit,
  Loader2,
  Download,
  Filter,
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const statusConfig = {
  draft: { label: "Entwurf", icon: Clock, className: "bg-slate-100 text-slate-600" },
  completed: { label: "Fertig", icon: CheckCircle2, className: "bg-emerald-50 text-emerald-600" },
  sent: { label: "Versendet", icon: Send, className: "bg-blue-50 text-blue-600" },
};

export default function ArchivePage() {
  const { isViewer } = useAuth();
  const [forms, setForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    filterForms();
  }, [forms, searchQuery, statusFilter]);

  const loadForms = async () => {
    try {
      const response = await getForms();
      setForms(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Formulare");
    } finally {
      setIsLoading(false);
    }
  };

  const filterForms = () => {
    let result = [...forms];

    if (statusFilter !== "all") {
      result = result.filter((f) => f.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.order_number.toLowerCase().includes(query) ||
          f.checked_by?.toLowerCase().includes(query)
      );
    }

    setFilteredForms(result);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      await deleteForm(deleteId);
      setForms((prev) => prev.filter((f) => f.id !== deleteId));
      toast.success("Formular gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleDownload = async (id, orderNumber) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/${id}/pdf`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      saveAs(blob, `${orderNumber}.pdf`);
      toast.success("PDF heruntergeladen");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Download fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="archive-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Archiv
        </h1>
        <p className="text-slate-500 mt-1">Alle gespeicherten Produktinformationen</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Suche nach Auftragsnummer oder Prüfer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-slate-200"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-[180px] border-slate-200">
                  <SelectValue placeholder="Status filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="draft">Entwürfe</SelectItem>
                  <SelectItem value="completed">Fertig</SelectItem>
                  <SelectItem value="sent">Versendet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forms List */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {filteredForms.length} Formular{filteredForms.length !== 1 ? "e" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredForms.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchQuery || statusFilter !== "all"
                  ? "Keine Formulare gefunden"
                  : "Noch keine Formulare vorhanden"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-medical">
                <thead>
                  <tr>
                    <th>Auftragsnummer</th>
                    <th>Packdatum</th>
                    <th>Geprüft von</th>
                    <th>Status</th>
                    <th>Erstellt</th>
                    <th className="text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map((form) => {
                    const status = statusConfig[form.status] || statusConfig.draft;
                    const StatusIcon = status.icon;
                    return (
                      <tr key={form.id} data-testid={`archive-item-${form.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-4 h-4 text-slate-600" />
                            </div>
                            <span className="font-medium">{form.order_number}</span>
                          </div>
                        </td>
                        <td>{form.packing_date}</td>
                        <td>{form.checked_by || "-"}</td>
                        <td>
                          <Badge className={`${status.className} gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </Badge>
                        </td>
                        <td>
                          {format(new Date(form.created_at), "dd.MM.yyyy", { locale: de })}
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`download-${form.id}`}
                              onClick={() => handleDownload(form.id, form.order_number)}
                              className="text-slate-500 hover:text-blue-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {!isViewer && (
                              <Link to={`/form/${form.id}`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`edit-${form.id}`}
                                  className="text-slate-500 hover:text-blue-600"
                                  title="Bearbeiten"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                            )}
                            {!isViewer && (
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-${form.id}`}
                                onClick={() => setDeleteId(form.id)}
                                className="text-slate-500 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Formular löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Das Formular wird
              dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
